import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePosts } from "../hooks/usePosts";
import { api } from "../api/client";
import { PostCard } from "../components/cards/PostCard";

type Ctx = { date: string };

/**
 * Filterable queue of posts awaiting approval.
 *
 * - Respects the top-bar date filter (was being ignored before — Review
 *   showed posts from every date).
 * - "All dates" toggle for when you're catching up on a backlog.
 * - Keyboard flow (j/k to move focus, a/r to approve/reject, Enter to
 *   open detail) so a long queue can be chewed through fast.
 */
export function Review() {
  const { date } = useOutletContext<Ctx>();
  const [allDates, setAllDates] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const scope = allDates ? undefined : date;
  const { data: pending = [], isLoading } = usePosts(
    scope,
    undefined,
    "pending",
  );

  const [focusIdx, setFocusIdx] = useState(0);

  // When the list shrinks (after approve/reject), keep focus in range.
  useEffect(() => {
    if (focusIdx >= pending.length && pending.length > 0) {
      setFocusIdx(pending.length - 1);
    }
  }, [focusIdx, pending.length]);

  const approve = useMutation({
    mutationFn: (id: string) => api.posts.approve(id, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.posts.reject(id, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when user is typing in a field
      const target = e.target as HTMLElement | null;
      if (target && /INPUT|TEXTAREA|SELECT/.test(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (pending.length === 0) return;

      const current = pending[focusIdx];
      switch (e.key) {
        case "j":
          e.preventDefault();
          setFocusIdx((i) => Math.min(i + 1, pending.length - 1));
          break;
        case "k":
          e.preventDefault();
          setFocusIdx((i) => Math.max(i - 1, 0));
          break;
        case "a":
          if (!current) return;
          e.preventDefault();
          approve.mutate(current.id);
          break;
        case "r":
          if (!current) return;
          e.preventDefault();
          reject.mutate(current.id);
          break;
        case "Enter":
          if (!current) return;
          e.preventDefault();
          navigate(`/posts/${current.id}`);
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, focusIdx, approve, reject, navigate]);

  // Scroll the focused card into view when it changes
  useEffect(() => {
    const el = document.querySelector<HTMLElement>(
      `[data-review-idx="${focusIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusIdx]);

  const scopeLabel = useMemo(() => {
    return allDates ? "alle Daten" : date;
  }, [allDates, date]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="headline text-6xl text-brand-text-dark">Review</h1>
          <p className="mt-2 text-sm text-brand-text-dark/60">
            {pending.length === 1
              ? `1 Post wartet auf Freigabe (${scopeLabel}).`
              : `${pending.length} Posts warten auf Freigabe (${scopeLabel}).`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-brand-text-dark/70 shadow-sm ring-1 ring-black/5">
            <input
              type="checkbox"
              checked={allDates}
              onChange={(e) => setAllDates(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Alle Daten
          </label>
          <KeyboardHint />
        </div>
      </div>

      {isLoading ? (
        <div className="text-brand-text-dark/60">Lade Queue…</div>
      ) : pending.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-12 text-center">
          <p className="text-sm text-brand-text-dark/60">
            {allDates
              ? "Keine offenen Reviews. Alles abgearbeitet."
              : `Keine offenen Reviews für ${date}. Aktiviere "Alle Daten" für den Backlog.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {pending.map((p, i) => (
            <div
              key={p.id}
              data-review-idx={i}
              className={
                i === focusIdx
                  ? "rounded-2xl ring-4 ring-brand-blue/60 ring-offset-4 ring-offset-brand-beige transition-all"
                  : "transition-all"
              }
              onMouseEnter={() => setFocusIdx(i)}
            >
              <PostCard post={p} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KeyboardHint() {
  return (
    <div className="hidden items-center gap-2 text-[10px] text-brand-text-dark/50 md:flex">
      <Key>J</Key><Key>K</Key> navigieren
      <span className="mx-1">·</span>
      <Key>A</Key> approve
      <span className="mx-1">·</span>
      <Key>R</Key> reject
      <span className="mx-1">·</span>
      <Key>⏎</Key> öffnen
    </div>
  );
}
function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-black/10 bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold shadow-sm">
      {children}
    </kbd>
  );
}
